import { useRef, useState } from "react";
import { Bot, Loader2, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { api, errMsg } from "@/lib/api";
import { aiOn, useSettings } from "@/context/SettingsContext";

const EXAMPLES = ["What's overdue right now?", "What did we do about BT last month?", "What's on Taya's plate this week?"];

export const AskButton = () => {
  const { team } = useSettings();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = useRef(null);
  const end = useRef(null);
  if (!aiOn(team, "ask")) return null;
  const ask = async (text = q) => {
    if (text.trim().length < 2 || busy) return;
    setMsgs((m) => [...m, { role: "user", text }]);
    setQ("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/ask", { question: text, session_id: sid.current }, { timeout: 60000 });
      sid.current = data.session_id;
      setMsgs((m) => [...m, { role: "ai", text: data.answer }]);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    setTimeout(() => end.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };
  const reset = () => { sid.current = null; setMsgs([]); };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="ask-ai-trigger" title="Ask your tasks" className="flex h-10 items-center gap-2 rounded-full border px-3 text-sm font-semibold text-cyan-300 transition-colors hover:bg-cyan-400/10">
        <Bot className="h-4 w-4" /> <span className="hidden md:inline">Ask AI</span>
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col border-white/10 bg-card sm:max-w-lg" data-testid="ask-ai-sheet">
          <div className="flex items-center justify-between pr-8">
            <SheetTitle className="flex items-center gap-2 font-display text-2xl"><Bot className="h-5 w-5 text-cyan-300" /> Ask your tasks</SheetTitle>
            {msgs.length > 0 && <button onClick={reset} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="ask-ai-reset"><Trash2 className="h-3 w-3" /> New chat</button>}
          </div>
          <SheetDescription>Ask anything about open and recently completed tasks and projects.</SheetDescription>
          <div className="scrollbar-thin -mx-2 flex-1 space-y-3 overflow-y-auto px-2 py-2" data-testid="ask-ai-messages">
            {!msgs.length && EXAMPLES.map((e) => <button key={e} onClick={() => ask(e)} className="block w-full rounded-xl border px-4 py-3 text-left text-sm text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground" data-testid="ask-ai-example">{e}</button>)}
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "ml-auto bg-primary text-primary-foreground" : "bg-white/[0.05]"}`} data-testid={`ask-ai-msg-${m.role}`}>{m.text}</div>
            ))}
            {busy && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Thinking…</p>}
            <div ref={end} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex items-center gap-2 border-t pt-3">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask a question…" className="h-10 flex-1 rounded-full border bg-background/60 px-4 text-sm outline-none focus:border-cyan-400/60" data-testid="ask-ai-input" />
            <button disabled={busy} className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-400 text-black transition-colors hover:bg-cyan-300 disabled:opacity-50" data-testid="ask-ai-send"><Send className="h-4 w-4" /></button>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
};
